import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import {
  getAttachmentsByTicket,
  uploadAttachment,
  deleteAttachment,
  getAttachmentUrl,
} from '../../../api/ticketAttachments';
import { useConfirm } from '../../common/ConfirmDialog';
import { useToast } from '../../common/Toast';
import './TicketAttachments.scss';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB (與 bucket file_size_limit 一致)

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TicketAttachments = ({ ticketId, canEdit }) => {
  const { profile } = useAuth();
  const { confirm } = useConfirm();
  const toast = useToast();
  const { usersMapByEmail } = useBetaData();
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState(null);

  useEffect(() => {
    loadAttachments();
  }, [ticketId]);

  const loadAttachments = async () => {
    setLoading(true);
    const { data } = await getAttachmentsByTicket(ticketId);
    setAttachments(data || []);
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let uploadFail = 0;
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`「${file.name}」超過 50MB 上傳限制`);
        uploadFail++;
        continue;
      }
      const { data, error } = await uploadAttachment(ticketId, profile.id, file);
      if (!error && data) {
        setAttachments((prev) => [data, ...prev]);
      } else {
        uploadFail++;
        const msg = error?.statusCode === '413' || error?.message?.includes('maximum allowed size')
          ? `「${file.name}」檔案過大，超過上傳限制`
          : `「${file.name}」上傳失敗`;
        toast.error(msg);
      }
    }
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (attachment) => {
    const confirmed = await confirm({
      title: '刪除附件',
      message: `確定要刪除「${attachment.file_name}」嗎？此操作無法復原。`,
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;
    const { error } = await deleteAttachment(attachment.id, attachment.file_path);
    if (!error) {
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    }
  };

  const handleDownload = async (attachment) => {
    const { data: url } = await getAttachmentUrl(attachment.file_path);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCopyLink = async (attachment) => {
    setCopyingId(attachment.id);
    const { data: url } = await getAttachmentUrl(attachment.file_path);
    if (url) {
      await navigator.clipboard.writeText(url);
    }
    setTimeout(() => setCopyingId(null), 1500);
  };

  return (
    <div className="ticket-attachments">
      <div className="attachments-header">
        <h3>
          附件
          {attachments.length > 0 && (
            <span className="count">{attachments.length}</span>
          )}
        </h3>
        {canEdit && (
          <>
            <button
              className="upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? '上傳中...' : '上傳檔案'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </>
        )}
      </div>

      {loading ? (
        <div className="attachments-skeleton">
          {[1, 2].map((i) => (
            <div key={i} className="skeleton-attachment">
              <div className="skeleton-icon" />
              <div className="skeleton-line w60 h14" />
            </div>
          ))}
        </div>
      ) : attachments.length === 0 ? (
        <div className="attachments-empty">尚無附件</div>
      ) : (
        <div className="attachments-list">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-item">
              <div className="file-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#666" strokeWidth="1.5">
                  <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z" />
                  <path d="M9 1v4h4" />
                </svg>
              </div>
              <div className="file-info" onClick={() => handleDownload(attachment)}>
                <span className="file-name">{attachment.file_name}</span>
                <span className="file-meta">
                  {formatFileSize(attachment.file_size)}
                  {attachment.uploader?.email && (() => {
                    const u = usersMapByEmail[attachment.uploader.email.toLowerCase()];
                    return ` - ${u?.name || attachment.uploader.email}`;
                  })()}
                </span>
              </div>
              <button
                className="copy-link-btn"
                onClick={() => handleCopyLink(attachment)}
                title={copyingId === attachment.id ? '已複製' : '複製連結'}
                disabled={copyingId === attachment.id}
              >
                {copyingId === attachment.id ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8.5l3 3 7-7" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                  </svg>
                )}
              </button>
              {canEdit && (
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(attachment)}
                  title="刪除"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketAttachments;
